import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import type { ReactNode } from 'react';
import { useQuery } from 'urql';
import BadgeMask from '@/components/Badge/BadgeMask';
import Avatar from '@/components/Custom/Avatar';
import * as Header from '@/components/Custom/Header';
import Box from '@/components/UI/Box';
import Text from '@/components/UI/Text';
import Icon from '@/components/Unistyles/Icon';
import { graphql } from '@/gql/graphql';

const GetBadgeDetailLayout = graphql(/* GraphQL */ `
  query GetBadgeDetailLayout($badgeId: uuid!) {
    badge_by_pk(id: $badgeId) {
      id
      title
      color
      permissions
      root_event {
        id
        title
      }
      team {
        id
        title
      }
    }
  }
`);

export default function BadgeDetailLayout({
  children,
}: {
  children: ReactNode;
}) {
  const { navigate } = useRouter();
  const { badgeId, eventId, teamId } = useLocalSearchParams<{
    badgeId?: string;
    eventId?: string;
    teamId?: string;
  }>();

  const [{ data }] = useQuery({
    query: GetBadgeDetailLayout,
    variables: {
      badgeId: badgeId ?? '',
    },
    pause: !badgeId,
  });

  const badge = data?.badge_by_pk;
  const contextLabel = badge?.team?.title ?? badge?.root_event?.title;
  const canEdit = Boolean(badge?.permissions?.edit);

  const headerTitle = () => (
    <Header.TitleContainer>
      <BadgeMask size="md" color={badge?.color}>
        <Avatar
          ring={false}
          size="md"
          color={badge?.color || ''}
          name={badge?.title || ''}
        />
      </BadgeMask>
      <Box flex>
        <Text
          numberOfLines={1}
          flexShrink={1}
          size="xl"
          testID="BadgeDetailHeaderTitle"
        >
          {badge?.title}
        </Text>
        <Box flex direction="horizontal" alignItems="center" space="sm">
          {badge?.team?.id ? (
            <Icon
              variant="typographySecondary"
              name="people-outline"
              size="sm"
            />
          ) : (
            <Icon
              variant="typographySecondary"
              name="globe-outline"
              size="sm"
            />
          )}
          <Text variant="typographySecondary" size="xs" numberOfLines={1}>
            {contextLabel || 'Badge'}
          </Text>
        </Box>
      </Box>
    </Header.TitleContainer>
  );

  const headerRight = () =>
    canEdit ? (
      <Header.End>
        <Header.ButtonGroup>
          <Header.Button
            accessibilityLabel="Badge settings"
            testID="BadgeDetailSettingsButton"
            onPress={() => {
              navigate({
                pathname: '/badge/[badgeId]',
                params: { badgeId, eventId, teamId },
              });
            }}
          >
            <Icon name="settings-outline" size="2xl" />
          </Header.Button>
        </Header.ButtonGroup>
      </Header.End>
    ) : null;

  return (
    <>
      <Stack.Screen
        options={{
          headerTitle,
          headerRight,
        }}
      />

      {children}
    </>
  );
}
